import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useSocket } from "@/components/SocketContext.jsx";
import api from "@/services/api";
import {
  SOCKET_DP_LOCATION_CLIENT_EMIT,
  DP_LIVE_LOCATION_REST_PATH,
} from "@/constants/deliveryLocationSocket";

/** Balance: fresher map vs battery/data; socket carries each tick when connected. */
const MIN_EMIT_INTERVAL_MS = 12_000;

/**
 * DP dashboard: geolocation → socket (real-time for admins) + POST set-dp-current-location (DB).
 * Socket uses the same VITE_CHAT_URL as chat — your server must relay dp_location_update → dp_live_location.
 */
export default function DeliveryPartnerLocationTracker() {
  const { user } = useSelector((state) => state.user);
  const socket = useSocket();
  const location = useLocation();
  const lastEmitRef = useRef(0);
  const watchIdRef = useRef(null);

  const isDeliveryPartner = user?.roles?.[0] === "Delivery Partner";

  useEffect(() => {
    if (!isDeliveryPartner || !user?.id) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    const send = (coords) => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastEmitRef.current < MIN_EMIT_INTERVAL_MS) return;
      lastEmitRef.current = now;

      const dropoffMatch = location.pathname.match(
        /\/assigned-delivery\/([^/]+)\/dropoff(?:\/|$)/
      );
      const assignmentId = dropoffMatch?.[1] ?? null;

      const payload = {
        userId: user.id,
        latitude: coords.latitude,
        longitude: coords.longitude,
        current_latitude: coords.latitude,
        current_longitude: coords.longitude,
        accuracy: coords.accuracy ?? null,
        recordedAt: new Date().toISOString(),
      };
      if (assignmentId != null) {
        const n = Number(assignmentId);
        payload.delivery_assigned_order_id = Number.isFinite(n)
          ? n
          : assignmentId;
      }

      if (socket?.connected) {
        socket.emit(SOCKET_DP_LOCATION_CLIENT_EMIT, payload);
      }

      api.post(DP_LIVE_LOCATION_REST_PATH, payload).catch(() => {});
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => send(pos.coords),
      () => {},
      {
        enableHighAccuracy: false,
        maximumAge: 60_000,
        timeout: 25_000,
      }
    );

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [isDeliveryPartner, user?.id, socket?.connected, location.pathname]);

  return null;
}
